'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useLayoutEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import SystemIcon from '../../DataDisplays/SystemIcon/SystemIcon.js';
import Typography from '../../Base/Typography/Typography.js';
import '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
/**
 * List.Root 컴포넌트는 목록의 컨테이너 역할을 합니다.
 *
 * @component
 * @param {ListRootProps} props
 * @param {boolean} [props.disableScroll=false] - 스크롤 비활성화 여부
 * @param {ReactNode} props.children - 목록 항목들
 * @param {string} [props.className] - 추가 CSS 클래스
 */ const Root = (_param)=>{
    var { children, className, disableScroll = false } = _param, rest = _object_without_properties(_param, [
        "children",
        "className",
        "disableScroll"
    ]);
    const ref = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    useLayoutEffect(()=>{
        const element = ref.current;
        if (!element) return;
        const { scrollHeight, clientHeight } = element;
        setIsScrollable(scrollHeight > clientHeight);
    }, []);
    return /*#__PURE__*/ jsx("ul", _object_spread_props(_object_spread({
        ref: ref,
        "data-testid": "design-system-list",
        className: twMerge('flex max-h-60 w-full min-w-[220px] flex-col rounded-xl bg-w-white p-2 shadow-graymedium', disableScroll ? 'max-h-full overflow-visible' : 'overflow-y-auto scrollbar-list', isScrollable && !disableScroll && 'pr-0', className)
    }, rest), {
        children: children
    }));
};
/**
 * List.Item 컴포넌트는 개별 목록 항목을 표시합니다.
 *
 * @component
 * @param {ListItemProps} props
 * @param {string} [props.text] - 항목 텍스트
 * @param {SystemIconName} [props.leadingIcon] - 항목 앞에 표시될 아이콘
 * @param {boolean} [props.selected=false] - 선택 상태
 * @param {boolean} [props.disabled=false] - 비활성화 상태
 * @param {boolean} [props.canceled=false] - 취소 상태
 * @param {boolean} [props.isFocused=false] - 포커스 상태
 * @param {ReactNode} props.children - 추가 컨텐츠
 * @param {string} [props.className] - 추가 CSS 클래스
 */ const Item = (_param)=>{
    var { leadingIcon, text, selected = false, disabled = false, canceled = false, children, className, isFocused = false } = _param, rest = _object_without_properties(_param, [
        "leadingIcon",
        "text",
        "selected",
        "disabled",
        "canceled",
        "children",
        "className",
        "isFocused"
    ]);
    const handleClick = (e)=>{
        var _rest_onClick;
        if (disabled) return;
        (_rest_onClick = rest.onClick) === null || _rest_onClick === void 0 ? void 0 : _rest_onClick.call(rest, e);
    };
    const itemStyle = twMerge(selected ? 'text-primary-500' : 'text-w-gray-600', canceled && 'text-w-red-500', disabled && 'text-w-gray-300');
    return /*#__PURE__*/ jsxs("li", _object_spread_props(_object_spread({
        className: twMerge('visible flex cursor-pointer select-none items-center gap-2 rounded-xl bg-w-white px-4 py-3', !disabled && isFocused && 'bg-primary-10', !disabled && 'hover:bg-primary-10', className)
    }, rest), {
        onClick: handleClick,
        "data-testid": "design-system-list-item",
        children: [
            leadingIcon && /*#__PURE__*/ jsx(SystemIcon, {
                name: leadingIcon,
                className: itemStyle,
                testId: "design-system-list-item-leading-icon"
            }),
            text && /*#__PURE__*/ jsx(Typography, {
                variant: "body1",
                className: itemStyle,
                "data-testid": "design-system-list-item-text",
                children: text
            }),
            children
        ]
    }));
};
/**
 * List 컴포넌트는 스크롤 가능한 목록을 표시하는 컴포넌트입니다.
 * Root와 Item 서브컴포넌트로 구성되어 있습니다.
 *
 * @component
 * @property {ListRootProps} Root - 목록 컨테이너
 * @property {ListItemProps} Item - 목록 항목
 *
 * @example
 * ```tsx
 * import List from './List';
 *
 * const MyList = () => (
 *   <List.Root>
 *     <List.Item
 *       text="목록 항목 1"
 *       leadingIcon="check"
 *     />
 *     <List.Item
 *       text="목록 항목 2"
 *       selected={true}
 *     />
 *     <List.Item
 *       text="비활성화된 항목"
 *       disabled={true}
 *     />
 *   </List.Root>
 * );
 * ```
 */ const List = {
    Root,
    Item
};

export { List as default };
